/**
 * useRBAC.js — Role-Based Access Control hook (Level-based, 4-tier)
 *
 * Roles (highest → lowest):
 *   super_admin → admin → core_crew → crew
 *
 * Each feature has ordered access levels defined in the roleMatrix
 * (stored in settings.data.roleMatrix). The matrix maps:
 *   feature → role → level
 *
 * Primary API:
 *   hasAccess(feature, minLevel, settings) — level-based check
 *   hasToolAccess(toolKey, settings)       — boolean tool check
 *   hasAction(action, settings)            — backward-compat wrapper
 *
 * Impersonation: super_admin/admin can "View As" any crew member.
 */

import { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './useAuth';

const RBACContext = createContext(null);

// ── Feature level definitions (must match server/features.js) ──────────
const FEATURES = {
  dashboard:  ['own_stats', 'full_stats'],
  jobs:       ['hidden', 'view_assigned', 'assigned_basic', 'assigned_full', 'all_edit', 'full'],
  quotes:     ['hidden', 'view_assigned', 'create_edit', 'full'],
  calculator: ['hidden', 'own_earnings', 'view_snap', 'full'],
  calendar:   ['own_schedule', 'view_all', 'full'],
  crm:        ['hidden', 'view_no_financials', 'edit_no_financials', 'full'],
  invoices:   ['hidden', 'view_assigned', 'create', 'manage_own', 'full'],
  payroll:    ['own_total', 'own_breakdown', 'full'],
  expenses:   ['hidden', 'submit_own', 'view_static', 'full'],
  time:       ['own_approved', 'own_trusted', 'full'],
  outreach:   ['hidden', 'full'],
  legal:      ['own_contracts', 'full'],
  wiki:       ['hidden', 'view', 'edit', 'full'],
  settings:   ['profile_only', 'company', 'full'],
};

const TOOL_KEYS = ['voicenotes', 'vehicle', 'activity', 'links', 'projects', 'openhouse'];

// ── Backward-compat: old hasAction keys → new hasAccess calls ──────────
const ACTION_TO_ACCESS = {
  manage_invoices:     { feature: 'invoices',  level: 'create' },
  view_financials:     { feature: 'payroll',   level: 'full' },
  manage_settings:     { feature: 'settings',  level: 'full' },
  void_jobs:           { feature: 'jobs',      level: 'full' },
  delete_own_jobs:     { feature: 'jobs',      level: 'all_edit' },
  delete_own_invoices: { feature: 'invoices',  level: 'manage_own' },
  edit_jobs:           { feature: 'jobs',       level: 'all_edit' },
  manage_team:         { feature: 'settings',   level: 'company' },
  manage_users:        { feature: 'settings',   level: 'company' },
  manage_crew_perms:   { feature: 'settings',   level: 'full' },
  preview_as:          { feature: 'settings',   level: 'company' },
  export_data:         { feature: 'settings',   level: 'full' },
  view_all_pay:        { feature: 'payroll',    level: 'full' },
  manage_crew_rates:   { feature: 'settings',   level: 'company' },
  manage_expenses:     { feature: 'expenses',   level: 'full' },
  edit_calculator:     { feature: 'calculator',  level: 'full' },
  create_edit_quotes:  { feature: 'quotes',     level: 'create_edit' },
  view_all_contracts:  { feature: 'legal',      level: 'full' },
  full_crm_access:     { feature: 'crm',        level: 'full' },
  view_crm:            { feature: 'crm',        level: 'full' },
  edit_crm:            { feature: 'crm',        level: 'edit_no_financials' },
  view_all_timelog:    { feature: 'time',       level: 'full' },
  view_all_actions:    { tool: 'activity' },
  view_void_notes:     { feature: 'jobs',       level: 'full' },
  // Always-allowed actions (return true for any authenticated user)
  view_own_pay:           { always: true },
  view_own_timelog:       { always: true },
  view_own_actions:       { always: true },
  view_own_contracts:     { always: true },
  view_crm_no_financials: { always: true },
};

// Legacy aliases
const PERM_ALIAS = {
  manage_users: 'manage_team',
  manage_crew_perms: 'manage_team',
  preview_as: 'manage_team',
  view_crm: 'full_crm_access',
  view_contracts: 'view_all_contracts',
  create_quote: 'create_edit_quotes',
  edit_quote: 'create_edit_quotes',
  void_job: 'void_jobs',
};

// Keep old PERMISSION_GROUPS export for PermissionsPanel backward compat
export const PERMISSION_GROUPS = {
  administration: [
    { key: 'manage_team', label: 'Manage Team', desc: 'Add/remove team members, link user accounts, change permissions' },
    { key: 'manage_settings', label: 'Manage Settings', desc: 'Edit company info, invoice settings, email templates, quote terms, integrations' },
    { key: 'export_data', label: 'Export & Import Data', desc: 'Download full data backups and restore from backup files' },
  ],
  financial: [
    { key: 'view_all_pay', label: 'View All Pay', desc: 'See pay splits, earnings, and payroll data for all team members' },
    { key: 'view_financials', label: 'Financial Reports', desc: 'Company Performance dashboard, Financial Report, and P&L statements' },
    { key: 'manage_crew_rates', label: 'Manage Crew Rates', desc: 'Set and edit hourly rates for crew members across work categories' },
    { key: 'manage_expenses', label: 'Manage Expenses', desc: 'Add, edit, and delete variable and recurring/static expenses' },
    { key: 'manage_invoices', label: 'Manage Invoices', desc: 'Create, edit, send, record payments, and manage client invoices' },
  ],
  jobs: [
    { key: 'edit_jobs', label: 'Edit Jobs', desc: 'Create new jobs, edit details, change status, manage assignments' },
    { key: 'void_jobs', label: 'Void Jobs', desc: 'Void/cancel active jobs and provide void reasons' },
    { key: 'edit_calculator', label: 'Edit Pay Calculator', desc: 'Modify pay calculator splits, percentages, and team assignments' },
  ],
  quotes: [
    { key: 'create_edit_quotes', label: 'Create & Edit Quotes', desc: 'Create and edit client quotes, manage quote terms' },
    { key: 'view_all_contracts', label: 'View All Contracts', desc: 'Access all team contracts and legal documents (not just own)' },
  ],
  crm: [
    { key: 'full_crm_access', label: 'Full CRM Access', desc: 'View client contact details, financial info, and all CRM data' },
    { key: 'edit_crm', label: 'Edit CRM Contacts', desc: 'Add new contacts, edit existing contacts, delete CRM entries' },
  ],
  time: [
    { key: 'view_all_timelog', label: 'View All Time Entries', desc: 'See time entries logged by all team members (not just own)' },
    { key: 'view_all_actions', label: 'View All Activity', desc: 'Full action log history showing all team member activities' },
  ],
  visibility: [
    { key: 'view_void_notes', label: 'View Internal Notes', desc: 'See void reasons, internal notes, and sensitive details on jobs' },
  ],
};

/**
 * Check if userLevel meets or exceeds requiredLevel for a feature.
 */
function meetsLevel(feature, userLevel, requiredLevel) {
  const levels = FEATURES[feature];
  if (!levels) return false;
  const userIdx = levels.indexOf(userLevel);
  const reqIdx = levels.indexOf(requiredLevel);
  if (userIdx === -1 || reqIdx === -1) return false;
  return userIdx >= reqIdx;
}

export function RBACProvider({ children }) {
  const [previewAsUserId, setPreviewAsUserId] = useState(null);
  const [previewAsRole, setPreviewAsRole] = useState(null);
  const { profile } = useAuth();

  /**
   * Check if user has at least `minLevel` access to `feature`.
   *
   * @param {string} feature - Feature key (e.g., 'jobs', 'invoices')
   * @param {string} minLevel - Minimum required level
   * @param {object} settings - App settings object (contains roleMatrix)
   * @returns {boolean}
   */
  const hasAccess = useCallback((feature, minLevel, settings = null) => {
    if (!profile) return false;

    const matrix = settings?.data?.roleMatrix;
    if (!matrix || !matrix[feature]) {
      // No matrix yet — fall back: super_admin/admin get full access
      return profile.role === 'super_admin' || profile.role === 'admin';
    }

    // Determine effective role (own vs impersonated)
    const effectiveRole = previewAsUserId ? (previewAsRole || 'crew') : profile.role;

    // Check per-user override first
    const overrides = settings?.data?.crewOverrides;
    const userId = previewAsUserId || profile.id;
    const overrideLevel = overrides?.[userId]?.[feature];

    const userLevel = overrideLevel || matrix[feature]?.[effectiveRole];
    if (!userLevel) return false;

    return meetsLevel(feature, userLevel, minLevel);
  }, [profile, previewAsUserId, previewAsRole]);

  /**
   * Check if user has access to a specific tool (boolean flag).
   */
  const hasToolAccess = useCallback((toolKey, settings = null) => {
    if (!profile) return false;
    if (!TOOL_KEYS.includes(toolKey)) return false;

    // super_admin always has access to all tools
    const effectiveRole = previewAsUserId ? (previewAsRole || 'crew') : profile.role;
    if (!previewAsUserId && profile.role === 'super_admin') return true;

    const matrix = settings?.data?.roleMatrix;
    if (!matrix?.tools) {
      return profile.role === 'super_admin' || profile.role === 'admin';
    }

    return matrix.tools[effectiveRole]?.[toolKey] === true;
  }, [profile, previewAsUserId, previewAsRole]);

  /**
   * Backward-compatible permission check (wraps hasAccess).
   * Maps old permission keys to new feature+level checks.
   */
  const hasAction = useCallback((action, settings = null) => {
    if (!profile) return false;

    // Resolve legacy aliases
    const resolved = PERM_ALIAS[action] || action;
    const mapping = ACTION_TO_ACCESS[resolved];

    if (!mapping) {
      // Unknown action — deny by default
      return false;
    }

    // Always-allowed actions
    if (mapping.always) return true;

    // Tool-based check
    if (mapping.tool) return hasToolAccess(mapping.tool, settings);

    // Feature+level check
    return hasAccess(mapping.feature, mapping.level, settings);
  }, [profile, hasAccess, hasToolAccess]);

  /**
   * Start impersonation — see the app as another user.
   * Only available to super_admin/admin roles.
   */
  const startImpersonation = useCallback((userId, userRole = 'crew') => {
    if (profile?.role === 'super_admin' || profile?.role === 'admin') {
      setPreviewAsUserId(userId);
      setPreviewAsRole(userRole);
    }
  }, [profile]);

  const stopImpersonation = useCallback(() => {
    setPreviewAsUserId(null);
    setPreviewAsRole(null);
  }, []);

  const isAdmin = useMemo(() => {
    return profile?.role === 'super_admin' || profile?.role === 'admin';
  }, [profile]);

  const isSuperAdmin = useMemo(() => {
    return profile?.role === 'super_admin';
  }, [profile]);

  const value = {
    hasAction,        // backward compat
    hasAccess,        // new primary API
    hasToolAccess,    // tool checks
    isAdmin,
    isSuperAdmin,
    previewAsUserId,
    isImpersonating: !!previewAsUserId,
    startImpersonation,
    stopImpersonation,
    PERMISSION_GROUPS,
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}

export function useRBAC() {
  const ctx = useContext(RBACContext);
  if (!ctx) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return ctx;
}
